import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { API_URL } from '../config/api';

/**
 * Admin-only engagement dashboard. Privacy-safe aggregates from /api/analytics/admin/engagement.
 * No individual-user views — only counts, averages, and page-level stats.
 */
const BUCKET_ORDER = ["0-30s", "30s-2m", "2m-5m", "5m-15m", "15m+"];

const formatSeconds = (s) => {
  if (s == null) return "—";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem ? `${m}m ${rem}s` : `${m}m`;
};

export const EngagementCard = () => {
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [newOnly, setNewOnly] = useState(true);
  const [data, setData] = useState(null);
  const [guideData, setGuideData] = useState(null);
  const [error, setError] = useState(null);

  const fetchStats = async (d, n) => {
    setLoading(true);
    setError(null);
    try {
      const [pageRes, guideRes] = await Promise.all([
        axios.get(
          `${API_URL}/api/analytics/admin/engagement?days=${d}&new_users_only=${n}`,
          { withCredentials: true }
        ),
        axios.get(
          `${API_URL}/api/analytics/admin/guide-engagement?days=${d}`,
          { withCredentials: true }
        ).catch(() => null),
      ]);
      setData(pageRes.data);
      setGuideData(guideRes?.data || null);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load engagement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats(days, newOnly);
  }, [days, newOnly]);

  const maxBucketCount = data?.bucket_distribution
    ? Math.max(1, ...data.bucket_distribution.map((b) => b.count))
    : 1;
  const maxPageVisits = data?.top_pages
    ? Math.max(1, ...data.top_pages.map((p) => p.visits))
    : 1;

  return (
    <div className="space-y-4" data-testid="admin-engagement-panel">
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <CardTitle>Engagement</CardTitle>
              <CardDescription>
                Privacy-safe aggregates. No IPs, no cookies, no clicks — only
                per-user page visits and session duration buckets.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {[1, 7, 30, 90].map((d) => (
                <Button
                  key={d}
                  variant={days === d ? "default" : "outline"}
                  size="sm"
                  onClick={() => setDays(d)}
                  data-testid={`engagement-range-${d}`}
                >
                  {d}d
                </Button>
              ))}
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground ml-1">
                <input
                  type="checkbox"
                  checked={newOnly}
                  onChange={(e) => setNewOnly(e.target.checked)}
                  data-testid="engagement-new-users-toggle"
                />
                New signups only
              </label>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center text-muted-foreground">Loading…</div>
          ) : error ? (
            <div className="py-6 text-sm text-red-600">{error}</div>
          ) : !data ? null : (
            <>
              {/* KPI row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <div className="rounded-lg border p-3" data-testid="engagement-kpi-users">
                  <div className="text-xs text-muted-foreground">Active Users</div>
                  <div className="text-2xl font-bold">{data.unique_users}</div>
                  {data.signups_in_window !== null && (
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      of {data.signups_in_window} signups
                    </div>
                  )}
                </div>
                <div className="rounded-lg border p-3" data-testid="engagement-kpi-visits">
                  <div className="text-xs text-muted-foreground">Total Visits</div>
                  <div className="text-2xl font-bold">{data.total_visits}</div>
                </div>
                <div className="rounded-lg border p-3" data-testid="engagement-kpi-avg">
                  <div className="text-xs text-muted-foreground">Avg Session</div>
                  <div className="text-2xl font-bold">
                    {formatSeconds(data.avg_session_seconds)}
                  </div>
                </div>
                <div className="rounded-lg border p-3" data-testid="engagement-kpi-median">
                  <div className="text-xs text-muted-foreground">Median Session</div>
                  <div className="text-2xl font-bold">
                    {formatSeconds(data.median_session_seconds)}
                  </div>
                </div>
              </div>

              {/* Session duration distribution */}
              <div className="mb-6">
                <div className="text-sm font-semibold mb-2">Session duration distribution</div>
                <div className="space-y-1.5">
                  {BUCKET_ORDER.map((b) => {
                    const bucket =
                      data.bucket_distribution.find((x) => x.bucket === b) || { count: 0 };
                    const pct = Math.round((bucket.count / maxBucketCount) * 100);
                    return (
                      <div key={b} className="flex items-center gap-3" data-testid={`engagement-bucket-${b}`}>
                        <div className="w-16 text-xs text-muted-foreground">{b}</div>
                        <div className="flex-1 h-5 bg-muted rounded overflow-hidden">
                          <div
                            className="h-full bg-primary/80"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <div className="w-10 text-right text-xs font-medium">{bucket.count}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Top pages */}
              <div>
                <div className="text-sm font-semibold mb-2">Top pages</div>
                {data.top_pages.length === 0 ? (
                  <div className="text-xs text-muted-foreground">No page visits in window.</div>
                ) : (
                  <div className="space-y-1.5">
                    {data.top_pages.map((p) => {
                      const pct = Math.round((p.visits / maxPageVisits) * 100);
                      return (
                        <div
                          key={p.page_key}
                          className="flex items-center gap-3"
                          data-testid={`engagement-page-${p.page_key}`}
                        >
                          <div className="w-36 text-xs truncate">{p.page_key}</div>
                          <div className="flex-1 h-4 bg-muted rounded overflow-hidden">
                            <div
                              className="h-full bg-emerald-500/70"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <div className="w-24 text-right text-xs text-muted-foreground">
                            {p.visits} · {formatSeconds(p.avg_seconds)} avg
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Ask Moe — guide engagement */}
      {guideData && guideData.guides && guideData.guides.length > 0 && (
        <Card data-testid="admin-guide-engagement-panel">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="text-[11px] font-semibold tracking-wider uppercase text-primary">Ask Moe</span>
              <span>Guide Engagement</span>
            </CardTitle>
            <CardDescription>
              Opens, product clicks, and click-through rate per action guide. Last {guideData.window_days} days.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
              <div className="rounded-lg border p-3" data-testid="guide-kpi-opens">
                <div className="text-xs text-muted-foreground">Total Opens</div>
                <div className="text-2xl font-bold">{guideData.total_opens}</div>
              </div>
              <div className="rounded-lg border p-3" data-testid="guide-kpi-clicks">
                <div className="text-xs text-muted-foreground">Product Clicks</div>
                <div className="text-2xl font-bold">{guideData.total_clicks}</div>
              </div>
              <div className="rounded-lg border p-3" data-testid="guide-kpi-ctr">
                <div className="text-xs text-muted-foreground">Overall CTR</div>
                <div className="text-2xl font-bold">
                  {guideData.total_opens
                    ? `${Math.round((100 * guideData.total_clicks) / guideData.total_opens)}%`
                    : "—"}
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {guideData.guides.map((g) => (
                <div
                  key={g.action_guide_key}
                  className="rounded-lg border border-border p-3"
                  data-testid={`guide-row-${g.action_guide_key}`}
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="font-semibold capitalize">{g.action_guide_key}</div>
                    <div className="text-xs text-muted-foreground">
                      {g.opens} opens · {g.product_clicks} clicks · {g.unique_users} users ·{" "}
                      <strong className="text-foreground">{g.click_through_rate}% CTR</strong>
                    </div>
                  </div>
                  {g.top_products.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {g.top_products.map((tp) => (
                        <span
                          key={tp.product_id}
                          className="text-[11px] px-2 py-0.5 rounded-full bg-muted text-foreground"
                        >
                          {tp.product_id} · {tp.clicks}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="text-xs text-muted-foreground px-1">
        We record only: user id, page slug, session id (resets per tab), and
        duration. No IP, no cookie, no clicks, no content. Retention: 90 days.
      </div>
    </div>
  );
};

export default EngagementCard;
